import {
  type PendingRevitOperationRead,
  type PendingRevitOperationV2,
} from '../../../api/grpc/operationProgressClient';
import { type ParameterAdditionResult } from './models';

/**
 * The immutable execution context a connector parameter invocation freezes at
 * click time. Recovery decisions compare the stored pending record against
 * this snapshot, never against live store values.
 */
export interface ConnectorExecutionContext {
  owner: 'connector-parameters';
  projectKey: string;
  expectedDocumentName: string | null;
  expectedDocumentPath: string | null;
  destination: 'project' | 'family';
  bindingKind: 'type' | 'instance';
}

export type PendingConnectorRecoveryDisposition =
  | { kind: 'none' }
  | { kind: 'exact-match'; record: PendingRevitOperationV2 }
  | { kind: 'mode-mismatch'; record: PendingRevitOperationV2 }
  | { kind: 'project-or-document-mismatch'; record: PendingRevitOperationV2 }
  | { kind: 'foreign-owner'; record: PendingRevitOperationV2 }
  | { kind: 'legacy-or-malformed'; operationId: string | null };

const normalizeIdentityPart = (value: string | null): string | null => {
  if (value === null) {
    return null;
  }

  const normalized = value.trim().toLowerCase().replace(/\//g, '\\');
  return normalized.length > 0 ? normalized : null;
};

const matchesDocumentIdentity = (
  record: PendingRevitOperationV2,
  context: ConnectorExecutionContext,
): boolean => {
  const recordPath = normalizeIdentityPart(record.expectedDocumentPath);
  const contextPath = normalizeIdentityPart(context.expectedDocumentPath);
  if (recordPath !== null && contextPath !== null) {
    return recordPath === contextPath;
  }

  const recordName = normalizeIdentityPart(record.expectedDocumentName);
  const contextName = normalizeIdentityPart(context.expectedDocumentName);
  if (recordName !== null && contextName !== null) {
    return recordName === contextName;
  }

  // No comparable identity pair — fail closed rather than assume same document.
  return false;
};

export function classifyPendingConnectorRecovery(
  read: PendingRevitOperationRead,
  context: ConnectorExecutionContext,
): PendingConnectorRecoveryDisposition {
  if (read.kind === 'none') {
    return { kind: 'none' };
  }

  if (read.kind === 'legacy') {
    return { kind: 'legacy-or-malformed', operationId: read.operationId };
  }

  if (read.kind === 'malformed') {
    return { kind: 'legacy-or-malformed', operationId: read.operationId };
  }

  const record = read.record;
  if (record.owner !== context.owner) {
    return { kind: 'foreign-owner', record };
  }

  if (record.projectKey !== context.projectKey || !matchesDocumentIdentity(record, context)) {
    return { kind: 'project-or-document-mismatch', record };
  }

  if (
    record.connectorTarget === null ||
    record.connectorTarget.destination !== context.destination ||
    record.connectorTarget.bindingKind !== context.bindingKind
  ) {
    return { kind: 'mode-mismatch', record };
  }

  return { kind: 'exact-match', record };
}

/**
 * An in-flight connector invocation: the frozen context it was started with
 * plus the promise for its result. A second invocation may join the promise
 * only when its own frozen context is fully equal.
 */
export interface ConnectorParameterInFlightEntry {
  context: ConnectorExecutionContext;
  promise: Promise<ParameterAdditionResult>;
}

export const areConnectorExecutionContextsEqual = (
  a: ConnectorExecutionContext,
  b: ConnectorExecutionContext,
): boolean =>
  a.owner === b.owner &&
  a.projectKey === b.projectKey &&
  a.expectedDocumentName === b.expectedDocumentName &&
  a.expectedDocumentPath === b.expectedDocumentPath &&
  a.destination === b.destination &&
  a.bindingKind === b.bindingKind;
